/**
 * Alert Settings Editor Component
 *
 * Unified component for editing alert settings for a specific tool.
 * Shows both Admin and Manager columns side by side for easy configuration.
 * Supports inline threshold configuration for specific tools (checkins, qualifications).
 */

import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, Plus, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export function AlertSettingsEditor({
  toolId,
  toolName,
  toolDescription,
  toolIcon: ToolIcon,
  alerts,
  adminSettings,
  managerSettings,
  onAdminChange,
  onManagerChange,
  disabled = false,
  // Threshold configuration (optional)
  thresholdConfig = null,
  onThresholdChange = null
}) {
  const [newThresholdDays, setNewThresholdDays] = useState('');

  const handleAlertChange = (role, alertId, channel, value) => {
    if (role === 'Admin') {
      const updatedSettings = {
        ...adminSettings,
        [alertId]: {
          ...(adminSettings?.[alertId] || {}),
          [channel]: value
        }
      };
      onAdminChange(updatedSettings);
    } else {
      const updatedSettings = {
        ...managerSettings,
        [alertId]: {
          ...(managerSettings?.[alertId] || {}),
          [channel]: value
        }
      };
      onManagerChange(updatedSettings);
    }
  };

  const handleAddExpiryThreshold = () => {
    const days = parseInt(newThresholdDays);
    if (isNaN(days) || days < 1 || days > 365) {
      toast.error('Enter a valid number between 1 and 365');
      return;
    }

    const currentDays = thresholdConfig?.expiry_warning_days || [];
    if (currentDays.includes(days)) {
      toast.error('This threshold already exists');
      return;
    }

    const newDays = [...currentDays, days].sort((a, b) => b - a);
    onThresholdChange?.({ ...thresholdConfig, expiry_warning_days: newDays });
    setNewThresholdDays('');
  };

  const handleRemoveExpiryThreshold = (days) => {
    const currentDays = thresholdConfig?.expiry_warning_days || [];
    const newDays = currentDays.filter(d => d !== days);
    onThresholdChange?.({ ...thresholdConfig, expiry_warning_days: newDays });
  };

  // Determine if this tool has thresholds to show
  const showCheckinThresholds = toolId === 'checkins' && thresholdConfig && onThresholdChange;
  const showQualificationThresholds = toolId === 'qualifications' && thresholdConfig && onThresholdChange;

  return (
    <div className="space-y-4">
      {/* Tool Header */}
      <div className="flex items-center gap-3 pb-3 border-b">
        {ToolIcon && <ToolIcon className="h-5 w-5 text-indigo-600" />}
        <div>
          <h3 className="text-lg font-semibold">{toolName}</h3>
          <p className="text-sm text-muted-foreground">{toolDescription}</p>
        </div>
      </div>

      {/* Alerts Table with Sticky Headers */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b bg-muted/30">
              <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">Alert</th>
              <th colSpan="2" className="text-center py-2 px-2 font-medium text-sm border-l bg-indigo-50">
                <div className="text-indigo-700">Admin</div>
              </th>
              <th colSpan="2" className="text-center py-2 px-2 font-medium text-sm border-l bg-green-50">
                <div className="text-green-700">Manager</div>
              </th>
            </tr>
            <tr className="border-b bg-muted/30">
              <th className="text-left py-2 px-4"></th>
              <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground border-l bg-indigo-50/50 w-16">
                App
              </th>
              <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground bg-indigo-50/50 w-16">
                Email
              </th>
              <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground border-l bg-green-50/50 w-16">
                App
              </th>
              <th className="text-center py-2 px-3 font-medium text-xs text-muted-foreground bg-green-50/50 w-16">
                Email
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(alerts).map(([alertId, alertDef]) => {
              const adminAlertSettings = adminSettings?.[alertId] || { app: false, email: false };
              const managerAlertSettings = managerSettings?.[alertId] || { app: false, email: false };

              return (
                <tr key={alertId} className="border-b last:border-b-0 hover:bg-muted/30">
                  <td className="py-3 px-4">
                    <div className="space-y-0.5">
                      <div className="font-medium text-sm">{alertDef.label}</div>
                      <div className="text-xs text-muted-foreground">{alertDef.description}</div>
                    </div>
                  </td>
                  {/* Admin App */}
                  <td className="py-3 px-3 text-center border-l bg-indigo-50/20">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={adminAlertSettings.app || false}
                        onCheckedChange={(checked) => handleAlertChange('Admin', alertId, 'app', checked)}
                        disabled={disabled}
                      />
                    </div>
                  </td>
                  {/* Admin Email */}
                  <td className="py-3 px-3 text-center bg-indigo-50/20">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={adminAlertSettings.email || false}
                        onCheckedChange={(checked) => handleAlertChange('Admin', alertId, 'email', checked)}
                        disabled={disabled}
                      />
                    </div>
                  </td>
                  {/* Manager App */}
                  <td className="py-3 px-3 text-center border-l bg-green-50/20">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={managerAlertSettings.app || false}
                        onCheckedChange={(checked) => handleAlertChange('Manager', alertId, 'app', checked)}
                        disabled={disabled}
                      />
                    </div>
                  </td>
                  {/* Manager Email */}
                  <td className="py-3 px-3 text-center bg-green-50/20">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={managerAlertSettings.email || false}
                        onCheckedChange={(checked) => handleAlertChange('Manager', alertId, 'email', checked)}
                        disabled={disabled}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Inline Thresholds for Check-ins */}
      {showCheckinThresholds && (
        <div className="mt-4 p-4 bg-orange-50/50 border border-orange-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <Label className="text-sm font-medium">Critical Shortage Threshold</Label>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Number of unmet requirements before marking as critical
          </p>
          <Input
            type="number"
            min="1"
            max="20"
            value={thresholdConfig?.critical_shortage_threshold || 3}
            onChange={(e) => onThresholdChange?.({
              ...thresholdConfig,
              critical_shortage_threshold: parseInt(e.target.value) || 3
            })}
            className="max-w-[120px]"
            disabled={disabled}
          />
        </div>
      )}

      {/* Inline Thresholds for Qualifications */}
      {showQualificationThresholds && (
        <div className="mt-4 p-4 bg-purple-50/50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-purple-600" />
            <Label className="text-sm font-medium">Expiry Warning Thresholds</Label>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Days before certificate expiry to send warning alerts
          </p>

          {/* Threshold badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            {(thresholdConfig?.expiry_warning_days || []).map((days) => (
              <Badge
                key={days}
                variant="secondary"
                className="bg-purple-100 text-purple-700 hover:bg-purple-200 pr-1"
              >
                {days} days
                {!disabled && (
                  <button
                    onClick={() => handleRemoveExpiryThreshold(days)}
                    className="ml-1.5 hover:text-purple-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
            {(thresholdConfig?.expiry_warning_days || []).length === 0 && (
              <span className="text-xs text-muted-foreground">No thresholds configured</span>
            )}
          </div>

          {/* Add threshold input */}
          {!disabled && (
            <div className="flex gap-2">
              <Input
                type="number"
                min="1"
                max="365"
                placeholder="Days"
                value={newThresholdDays}
                onChange={(e) => setNewThresholdDays(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddExpiryThreshold();
                  }
                }}
                className="max-w-[100px]"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddExpiryThreshold}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Alert Settings Summary Component
 *
 * Shows a summary of enabled/disabled alerts for a tool
 */
export function AlertSettingsSummary({ toolName, adminSettings, managerSettings, alerts }) {
  if (!alerts) {
    return (
      <div className="text-sm text-muted-foreground">
        No alerts defined
      </div>
    );
  }

  const countEnabled = (settings) => {
    if (!settings) return { app: 0, email: 0 };
    let appCount = 0;
    let emailCount = 0;
    Object.values(settings).forEach(config => {
      if (config?.app) appCount++;
      if (config?.email) emailCount++;
    });
    return { app: appCount, email: emailCount };
  };

  const adminCounts = countEnabled(adminSettings);
  const managerCounts = countEnabled(managerSettings);
  const totalAlerts = Object.keys(alerts).length;

  return (
    <div className="flex items-center gap-4 text-xs">
      <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-indigo-50">
        <span className="text-indigo-600 font-medium">Admin:</span>
        <span className="text-indigo-700">{adminCounts.app} app, {adminCounts.email} email</span>
      </div>
      <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-50">
        <span className="text-green-600 font-medium">Manager:</span>
        <span className="text-green-700">{managerCounts.app} app, {managerCounts.email} email</span>
      </div>
    </div>
  );
}
